import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Badge, InputGroup, Pagination } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';

const getApiUrl = () => localStorage.getItem('apiUrl') || 'http://localhost:8000';
const fmt = (n) => (Number(n) || 0).toLocaleString('id-ID');

const LaporanRekapJurnalKasir = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation & Menus
  const [menus, setMenus] = useState([]);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [time, setTime] = useState({ date: '', clock: '' });

  // Filter States
  const today = new Date();
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [periodeAwal, setPeriodeAwal] = useState(fmtDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [periodeAkhir, setPeriodeAkhir] = useState(fmtDate(today));
  const [autoProcess, setAutoProcess] = useState(false);

  // Search & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [caraBayarFilter, setCaraBayarFilter] = useState('ALL'); // ALL, Tunai, Piutang
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Data States
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [listData, setListData] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadFilename, setDownloadFilename] = useState('');

  // Clock & Navbar initialization
  useEffect(() => {
    const savedMenu = localStorage.getItem('mainmenu');
    if (savedMenu) {
      try { setMenus(JSON.parse(savedMenu)); } catch (e) { setMenus([]); }
    }
    const updateTime = () => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const mi = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      setTime({ date: `${dd}/${mm}/${now.getFullYear()}`, clock: `${hh}:${mi}:${ss}` });
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Silent download helper
  const triggerDownload = (url, filename) => {
    if (!url) return;
    const fullUrl = url.startsWith('http') ? url : `${getApiUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.setAttribute('download', filename || url.split('/').pop() || 'Laporan_Rekap_Jurnal.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch Data from Backend
  const handleFetchData = async (isDownloadOnly = false) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('token') || '';
      const payload = {
        periode1: periodeAwal,
        periode2: periodeAkhir,
        auto_process: autoProcess
      };

      const res = await fetch(`${getApiUrl()}/laporan_rekap_jurnal_kasir?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`HTTP Error: ${res.status} ${res.statusText}`);
      }

      const json = await res.json();
      if (json && (json.status === 1 || json.items)) {
        const items = Array.isArray(json.items) ? json.items : [];
        setListData(items);
        setGrandTotal(json.grand_total || null);
        const dlUrl = json.download_url || json.download_url_xlsx || '';
        setDownloadUrl(dlUrl);
        const fName = dlUrl ? dlUrl.split('/').pop() : `Lap_Rekap_Jurnal_${periodeAwal}_${periodeAkhir}.xlsx`;
        setDownloadFilename(fName);
        setCurrentPage(1);

        if (isDownloadOnly && dlUrl) {
          triggerDownload(dlUrl, fName);
        }
      } else {
        setErrorMsg(json.msg || 'Gagal menyusun laporan');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setErrorMsg(err.message || 'Terjadi kesalahan saat memuat data dari server');
    } finally {
      setIsLoading(false);
    }
  };

  // Unduh Excel Click Handler
  const handleUnduhExcel = () => {
    if (downloadUrl) {
      triggerDownload(downloadUrl, downloadFilename);
    } else {
      handleFetchData(true);
    }
  };

  // Filter & Search
  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return listData.filter((item) => {
      // Filter Cara Bayar
      if (caraBayarFilter !== 'ALL') {
        const cb = (item.cara_bayar || '').toUpperCase();
        if (caraBayarFilter === 'Tunai' && cb !== 'TUNAI') return false;
        if (caraBayarFilter === 'Piutang' && cb !== 'PIUTANG') return false;
      }
      // Filter Pencarian Cepat
      if (!q) return true;
      const noNota = String(item.no_nota || '').toLowerCase();
      const noKuitansi = String(item.no_kuitansi || '').toLowerCase();
      const noRM = String(item.no_kirs || '').toLowerCase();
      const nmPasien = String(item.nm_pasien || '').toLowerCase();
      const nmPrs = String(item.nm_prs || '').toLowerCase();
      const klas = String(item.klasifikasi || '').toLowerCase();
      return (
        noNota.includes(q) ||
        noKuitansi.includes(q) ||
        noRM.includes(q) ||
        nmPasien.includes(q) ||
        nmPrs.includes(q) ||
        klas.includes(q)
      );
    });
  }, [listData, searchQuery, caraBayarFilter]);

  // Pagination Slice
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentItems = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  // Dynamic Subtotal based on filtered data
  const subtotalFiltered = useMemo(() => {
    return filteredData.reduce(
      (acc, it) => {
        acc.adm += Number(it.adm) || 0;
        acc.farmasi += (Number(it.farmasi) || 0) + (Number(it.apotek) || 0);
        acc.lab += Number(it.lab) || 0;
        acc.radiologi += Number(it.radiologi) || 0;
        acc.poliklinik += Number(it.poliklinik) || 0;
        acc.ugd += Number(it.ugd) || 0;
        acc.ok += Number(it.ok) || 0;
        acc.dokter += Number(it.dokter) || 0;
        acc.total += Number(it.total) || 0;
        return acc;
      },
      { adm: 0, farmasi: 0, lab: 0, radiologi: 0, poliklinik: 0, ugd: 0, ok: 0, dokter: 0, total: 0 }
    );
  }, [filteredData]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9', fontFamily: "'Inter', 'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      
      {/* NAVBAR MODERN */}
      <nav className="navbar navbar-expand-lg shadow-sm" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)', borderBottom: 'none', padding: '0 1.5rem', height: '50px' }}>
        <div className="container-fluid px-0">
          <a className="navbar-brand d-flex align-items-center gap-2" href="/home" style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff' }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', padding: '5px 9px', borderRadius: '7px' }}>
              <i className="bi bi-journal-bookmark-fill text-warning fs-6"></i>
            </div>
            SIMRS2
          </a>
          <button className="navbar-toggler border-0" type="button" onClick={() => setIsNavOpen(!isNavOpen)}>
            <i className="bi bi-list text-white fs-5"></i>
          </button>
          <div className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`}>
            <ul className="navbar-nav me-auto mb-1 mb-lg-0 flex-row gap-2 ms-3">
              {menus.map((item) => {
                const isActive = location.pathname.toLowerCase() === (item.lnk || '').toLowerCase();
                return (
                  <li className="nav-item" key={item.id_mn}>
                    <a href={item.lnk || '#'} className="nav-link py-1 px-2 rounded" style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.7)', fontSize: '0.75rem', fontWeight: isActive ? 600 : 500, background: isActive ? 'rgba(13,148,136,0.4)' : 'transparent' }}>
                      {item.nm_mn}
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="d-flex align-items-center gap-3">
              <span className="text-white-50 fw-medium d-none d-lg-flex align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-clock"></i> {time.clock}
              </span>
              <Button 
                variant="outline-light" 
                size="sm" 
                className="d-flex align-items-center gap-1 py-1 text-white border-white-50" 
                style={{ fontSize: '0.75rem', borderRadius: '6px', fontWeight: 600, background: 'rgba(255,255,255,0.1)' }} 
                onClick={() => navigate('/home')}
              >
                <i className="bi bi-house-door-fill"></i>
                Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* SUB-HEADER / BANNER */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)', padding: '0.75rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <Button
              size="sm"
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: '7px', padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600 }}
            >
              <i className="bi bi-arrow-left me-1"></i>Kembali
            </Button>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '8px', padding: '8px 10px' }}>
              <i className="bi bi-file-earmark-spreadsheet-fill text-warning fs-5"></i>
            </div>
            <div>
              <div className="text-white fw-bold" style={{ fontSize: '1.05rem', letterSpacing: '0.3px' }}>
                LAPORAN REKAPITULASI JURNAL KASIR / BENDAHARA
              </div>
              <div className="text-white-50" style={{ fontSize: '0.72rem' }}>
                Rekap Transaksi Pembayaran Kasir Berdasarkan Unit Pelayanan, Komisi & Akun Keuangan
              </div>
            </div>
          </div>
          <div className="text-white-50" style={{ fontSize: '0.75rem' }}>
            <i className="bi bi-calendar3 me-1"></i>{time.date}
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <Container fluid className="p-3 flex-grow-1 d-flex flex-column" style={{ maxWidth: '1750px' }}>

        {/* 1. FILTER & KONTROL (TOP BAR) */}
        <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: '12px' }}>
          <Card.Body className="p-3">
            <Row className="g-3 align-items-end">
              {/* Periode Tanggal Awal */}
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Group>
                  <Form.Label className="text-muted small fw-bold mb-1" style={{ fontSize: '0.73rem' }}>
                    <i className="bi bi-calendar-event me-1 text-primary"></i>TANGGAL AWAL
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periodeAwal}
                    onChange={(e) => setPeriodeAwal(e.target.value)}
                    style={{ fontSize: '0.8rem', borderRadius: '7px', fontWeight: 600, border: '1.5px solid #cbd5e1' }}
                  />
                </Form.Group>
              </Col>

              {/* Periode Tanggal Akhir */}
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Group>
                  <Form.Label className="text-muted small fw-bold mb-1" style={{ fontSize: '0.73rem' }}>
                    <i className="bi bi-calendar-check me-1 text-primary"></i>TANGGAL AKHIR
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periodeAkhir}
                    onChange={(e) => setPeriodeAkhir(e.target.value)}
                    style={{ fontSize: '0.8rem', borderRadius: '7px', fontWeight: 600, border: '1.5px solid #cbd5e1' }}
                  />
                </Form.Group>
              </Col>

              {/* Checkbox Auto Process */}
              <Col xs={12} sm={6} md={3} lg={2}>
                <div className="pb-1">
                  <Form.Check
                    type="checkbox"
                    id="auto-process-check"
                    label={
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                        Sinkronisasi Otomatis
                      </span>
                    }
                    checked={autoProcess}
                    onChange={(e) => setAutoProcess(e.target.checked)}
                    title="Jalankan kalkulasi dan rekap ulang data terbaru secara otomatis"
                  />
                  <div style={{ fontSize: '0.66rem', color: '#64748b' }}>
                    Auto-process data mutasi kas
                  </div>
                </div>
              </Col>

              {/* Tombol Aksi (Tampilkan Data & Unduh Excel) */}
              <Col xs={12} sm={6} md={3} lg={3} className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isLoading}
                  onClick={() => handleFetchData(false)}
                  className="d-flex align-items-center justify-content-center gap-2 flex-grow-1"
                  style={{
                    borderRadius: '8px',
                    padding: '7px 14px',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)'
                  }}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span>Memuat...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-search"></i>
                      <span>Tampilkan Data</span>
                    </>
                  )}
                </Button>

                <Button
                  variant="success"
                  size="sm"
                  disabled={isLoading}
                  onClick={handleUnduhExcel}
                  className="d-flex align-items-center justify-content-center gap-2"
                  style={{
                    borderRadius: '8px',
                    padding: '7px 14px',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.25)',
                    whiteSpace: 'nowrap'
                  }}
                  title="Unduh berkas Excel .xlsx"
                >
                  <i className="bi bi-file-earmark-excel-fill"></i>
                  <span>Unduh Excel</span>
                </Button>
              </Col>

              {/* Pencarian Cepat Live */}
              <Col xs={12} md={12} lg={3}>
                <Form.Group>
                  <Form.Label className="text-muted small fw-bold mb-1" style={{ fontSize: '0.73rem' }}>
                    <i className="bi bi-funnel-fill me-1 text-secondary"></i>PENCARIAN CEPAT
                  </Form.Label>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#fff', borderColor: '#cbd5e1' }}>
                      <i className="bi bi-search text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Cari Nota, Kuitansi, No. RM, Pasien..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ fontSize: '0.8rem', borderColor: '#cbd5e1' }}
                    />
                    {searchQuery && (
                      <Button variant="outline-secondary" onClick={() => setSearchQuery('')} title="Reset">
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* 2. SUMMARY KPI CARDS */}
        <Row className="g-3 mb-3">
          {[
            {
              label: 'TOTAL TRANSAKSI',
              value: `${filteredData.length} Data`,
              sub: listData.length !== filteredData.length ? `Dari total ${listData.length} data` : 'Sesuai filter periode',
              color: '#4f46e5',
              bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
              icon: 'bi-receipt'
            },
            {
              label: 'TOTAL PENDAPATAN',
              value: `Rp ${fmt(grandTotal ? grandTotal.total : subtotalFiltered.total)}`,
              sub: 'Total seluruh unit pelayanan',
              color: '#059669',
              bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              icon: 'bi-cash-stack'
            },
            {
              label: 'TOTAL JASA DOKTER',
              value: `Rp ${fmt(grandTotal ? grandTotal.dokter : subtotalFiltered.dokter)}`,
              sub: 'Alokasi komisi medis dokter',
              color: '#d97706',
              bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              icon: 'bi-person-heart'
            },
            {
              label: 'FARMASI & APOTEK',
              value: `Rp ${fmt(grandTotal ? ((grandTotal.farmasi || 0) + (grandTotal.apotek || 0)) : subtotalFiltered.farmasi)}`,
              sub: 'Total penjualan obat & alkes',
              color: '#0284c7',
              bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              icon: 'bi-capsule'
            }
          ].map((c, idx) => (
            <Col xs={12} sm={6} xl={3} key={idx}>
              <div
                className="shadow-sm h-100 p-3"
                style={{
                  borderRadius: '12px',
                  background: c.bg,
                  borderLeft: `5px solid ${c.color}`
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: c.color, letterSpacing: '0.5px' }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: c.color, marginTop: '2px' }}>
                      {c.value}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '3px' }}>
                      {c.sub}
                    </div>
                  </div>
                  <div
                    style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '9px',
                      background: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: c.color,
                      boxShadow: '0 2px 5px rgba(0,0,0,0.06)'
                    }}
                  >
                    <i className={`bi ${c.icon} fs-5`}></i>
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* 3. ERROR & FEEDBACK ALERTS */}
        {errorMsg && (
          <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 shadow-sm border-0 mb-3" style={{ borderRadius: '8px', fontSize: '0.8rem' }}>
            <i className="bi bi-exclamation-octagon-fill fs-5"></i>
            <div>{errorMsg}</div>
          </div>
        )}

        {/* 4. DATA TABLE CARD */}
        <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column mb-3" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          
          {/* Table Header Bar */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2 bg-white border-bottom">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-table me-1 text-primary"></i>Rincian Jurnal Kasir
              </span>
              <Badge bg="secondary" pill style={{ fontSize: '0.7rem' }}>
                {filteredData.length} Baris
              </Badge>
            </div>

            {/* Quick Filter Cara Bayar */}
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '0.73rem', color: '#64748b', fontWeight: 600 }}>Jenis Bayar:</span>
              <div className="btn-group btn-group-sm" role="group">
                {['ALL', 'Tunai', 'Piutang'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setCaraBayarFilter(mode); setCurrentPage(1); }}
                    className={`btn ${caraBayarFilter === mode ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.72rem', padding: '2px 10px', fontWeight: 600 }}
                  >
                    {mode === 'ALL' ? 'Semua' : mode}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Table Body Scrollable */}
          <div className="flex-grow-1 overflow-auto" style={{ maxHeight: 'calc(100vh - 430px)', minHeight: '320px', backgroundColor: '#fff' }}>
            {isLoading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <Spinner animation="border" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
                <span className="mt-3 text-muted fw-semibold" style={{ fontSize: '0.85rem' }}>
                  Sedang memuat data transaksi jurnal kasir...
                </span>
                <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                  Proses ini dapat memakan beberapa detik untuk rentang tanggal yang luas.
                </span>
              </div>
            ) : listData.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary opacity-50"></i>
                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>Belum ada data yang ditampilkan</div>
                <div style={{ fontSize: '0.75rem' }}>Silakan tentukan periode tanggal dan klik tombol <strong>Tampilkan Data</strong></div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-search fs-1 d-block mb-2 text-secondary opacity-50"></i>
                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>Data tidak ditemukan</div>
                <div style={{ fontSize: '0.75rem' }}>Tidak ada transaksi yang cocok dengan kata kunci <em>"{searchQuery}"</em></div>
              </div>
            ) : (
              <Table hover responsive bordered className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 5, background: '#003366', color: '#ffffff' }}>
                  <tr className="text-center align-middle">
                    <th style={{ minWidth: '40px', background: '#003366', color: '#fff' }}>NO</th>
                    <th style={{ minWidth: '150px', background: '#003366', color: '#fff' }}>NO KUITANSI</th>
                    <th style={{ minWidth: '140px', background: '#003366', color: '#fff' }}>NO NOTA</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>TGL BAYAR</th>
                    <th style={{ minWidth: '80px', background: '#003366', color: '#fff' }}>NO RM</th>
                    <th style={{ minWidth: '180px', background: '#003366', color: '#fff' }}>NAMA PASIEN</th>
                    <th style={{ minWidth: '80px', background: '#003366', color: '#fff' }}>CARA BAYAR</th>
                    <th style={{ minWidth: '90px', background: '#003366', color: '#fff' }}>ADM</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>APOTEK</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>FARMASI</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>LAB</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>RADIOLOGI</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>POLIKLINIK</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>UGD</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>OK</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>ICU</th>
                    <th style={{ minWidth: '95px', background: '#003366', color: '#fff' }}>VK</th>
                    <th style={{ minWidth: '110px', background: '#002244', color: '#fbbf24', fontWeight: 800 }}>TOTAL</th>
                    <th style={{ minWidth: '100px', background: '#003366', color: '#fff' }}>DOKTER</th>
                    <th style={{ minWidth: '90px', background: '#003366', color: '#fff' }}>PEMASARAN</th>
                    <th style={{ minWidth: '90px', background: '#003366', color: '#fff' }}>KEUANGAN</th>
                    <th style={{ minWidth: '85px', background: '#003366', color: '#fff' }}>IT</th>
                    <th style={{ minWidth: '130px', background: '#003366', color: '#fff' }}>PENJAMIN / PRS</th>
                    <th style={{ minWidth: '100px', background: '#003366', color: '#fff' }}>KLASIFIKASI</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, idx) => {
                    const rowNo = (currentPage - 1) * itemsPerPage + idx + 1;
                    return (
                      <tr key={`${item.no_kuitansi}_${item.no_nota}_${idx}`}>
                        <td className="text-center fw-semibold text-muted">{rowNo}</td>
                        <td className="fw-semibold text-primary">{item.no_kuitansi || '-'}</td>
                        <td>{item.no_nota || '-'}</td>
                        <td className="text-center">{item.tgl_bayar || item.tgl_kuitansi || '-'}</td>
                        <td className="text-center font-monospace fw-semibold">{item.no_kirs || '-'}</td>
                        <td className="fw-semibold text-dark">{item.nm_pasien || '-'}</td>
                        <td className="text-center">
                          <span
                            className={`badge ${item.cara_bayar === 'Piutang' ? 'bg-warning-subtle text-warning-emphasis border border-warning' : 'bg-success-subtle text-success border border-success'}`}
                            style={{ fontSize: '0.68rem', padding: '3px 8px' }}
                          >
                            {item.cara_bayar || 'Tunai'}
                          </span>
                        </td>
                        <td className="text-end font-monospace">{item.adm ? fmt(item.adm) : '-'}</td>
                        <td className="text-end font-monospace">{item.apotek ? fmt(item.apotek) : '-'}</td>
                        <td className="text-end font-monospace">{item.farmasi ? fmt(item.farmasi) : '-'}</td>
                        <td className="text-end font-monospace">{item.lab ? fmt(item.lab) : '-'}</td>
                        <td className="text-end font-monospace">{item.radiologi ? fmt(item.radiologi) : '-'}</td>
                        <td className="text-end font-monospace">{item.poliklinik ? fmt(item.poliklinik) : '-'}</td>
                        <td className="text-end font-monospace">{item.ugd ? fmt(item.ugd) : '-'}</td>
                        <td className="text-end font-monospace">{item.ok ? fmt(item.ok) : '-'}</td>
                        <td className="text-end font-monospace">{item.icu ? fmt(item.icu) : '-'}</td>
                        <td className="text-end font-monospace">{item.vk ? fmt(item.vk) : '-'}</td>
                        <td className="text-end font-monospace fw-bold text-primary" style={{ backgroundColor: '#f0fdf4' }}>
                          {fmt(item.total)}
                        </td>
                        <td className="text-end font-monospace">{item.dokter ? fmt(item.dokter) : '-'}</td>
                        <td className="text-end font-monospace">{item.pemasaran ? fmt(item.pemasaran) : '-'}</td>
                        <td className="text-end font-monospace">{item.keuangan ? fmt(item.keuangan) : '-'}</td>
                        <td className="text-end font-monospace">{item.it ? fmt(item.it) : '-'}</td>
                        <td className="text-truncate" style={{ maxWidth: '160px' }} title={item.nm_prs || ''}>
                          {item.nm_prs || '-'}
                        </td>
                        <td>{item.klasifikasi || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* GRAND TOTAL ROW */}
                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 4, background: '#cbd5e1', fontWeight: 700 }}>
                  <tr className="align-middle">
                    <td colSpan={7} className="text-center text-dark" style={{ background: '#cbd5e1' }}>
                      TOTAL HALAMAN INI / KESELURUHAN
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.adm : subtotalFiltered.adm)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.apotek : 0)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.farmasi : subtotalFiltered.farmasi)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.lab : subtotalFiltered.lab)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.radiologi : subtotalFiltered.radiologi)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.poliklinik : subtotalFiltered.poliklinik)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.ugd : subtotalFiltered.ugd)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.ok : subtotalFiltered.ok)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.icu : 0)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.vk : 0)}
                    </td>
                    <td className="text-end font-monospace text-primary fw-bolder" style={{ background: '#bbf7d0', fontSize: '0.8rem' }}>
                      {fmt(grandTotal ? grandTotal.total : subtotalFiltered.total)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.dokter : subtotalFiltered.dokter)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.pemasaran : 0)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.keuangan : 0)}
                    </td>
                    <td className="text-end font-monospace" style={{ background: '#cbd5e1' }}>
                      {fmt(grandTotal ? grandTotal.it : 0)}
                    </td>
                    <td colSpan={2} style={{ background: '#cbd5e1' }}></td>
                  </tr>
                </tfoot>
              </Table>
            )}
          </div>

          {/* Table Footer & Pagination */}
          {filteredData.length > itemsPerPage && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2 bg-white border-top">
              <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                Menampilkan {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredData.length)} dari {filteredData.length} data
              </span>
              <Pagination size="sm" className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card>
      </Container>
    </div>
  );
};

export default LaporanRekapJurnalKasir;
